// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { DataCacheToolType } from '../DataCacheToolEnums';
import { Color, Pen, SolidBrush } from '../../../Commons/Graphics';
import { DataCacheTool } from '../DataCacheTool';
import { DynProperty, ColorStyleWidth } from '../../DynProperty';
import { getPriceTypeKey, PriceType } from '@shared/utils/History/CashItemUtils';
import { Resources } from '@shared/localizations/Resources';
import { BarIndex } from '../History/HistoryConstants';
import { createContextMenuItemsFromEnum } from '@shared/utils/ContextMenuUtils';
export class RegressionChannelDataCacheTool extends DataCacheTool {
    constructor() {
        super();
        this.needRecalculate = false;
        this.coef = null;
        this.PriceType = PriceType.Open;
        this.ToolType = DataCacheToolType.RegressionChannel;
        this.InitPoints(2);
    }
    InitBlock() {
        super.InitBlock();
        this.colorAlpha = 50;
        this.IsBackground = true;
        this.DownLinePen = new Pen(Color.Red);
        this.UpLinePen = new Pen(Color.Red);
        this.UpBrushColor = Color.Blue;
        this.DownBrushColor = Color.Red;
        this.UpperDeviation = 1;
        this.LowerDeviation = -1;
        this.needRecalculate = true;
        this.DownLinePen.Width = 1;
        this.UpLinePen.Width = 1;
    }
    get PriceTypeInt() {
        switch (this.PriceType) {
            case PriceType.Open:
                return BarIndex.Open;
            case PriceType.Close:
                return BarIndex.Close;
            case PriceType.High:
                return BarIndex.High;
            case PriceType.Low:
                return BarIndex.Low;
            case PriceType.Medium:
                return BarIndex.Median;
            case PriceType.Typical:
                return BarIndex.Typical;
            case PriceType.Weighted:
                return BarIndex.Weighted;
            default:
                return -1;
        }
    }
    MinPointsCount() {
        return 2;
    }
    MaxPointsCount() {
        return 2;
    }
    set UpBrushColor(value) {
        if (this.upBrushColor === value)
            return;
        this.upBrushColor = value;
        this.UpBrush = new SolidBrush(Color.FromArgb(this.colorAlpha, this.upBrushColor));
    }
    get UpBrushColor() {
        return this.upBrushColor;
    }
    set DownBrushColor(value) {
        if (this.downBrushColor === value)
            return;
        this.downBrushColor = value;
        this.DownBrush = new SolidBrush(Color.FromArgb(this.colorAlpha, this.downBrushColor));
    }
    get DownBrushColor() {
        return this.downBrushColor;
    }
    get TopColor() {
        return this.UpBrushColor;
    }
    set TopColor(value) {
        this.UpBrushColor = value;
    }
    get BottomColor() {
        return this.DownBrushColor;
    }
    set BottomColor(value) {
        this.DownBrushColor = value;
    }
    Properties() {
        const props = super.Properties();
        let prop = DynProperty.getPropertyByName(props, 'Line');
        if (prop) {
            prop.propertyComment = Resources.getResource('property.chart.regression.RegressionLine');
            prop.sortIndex = 6;
        }
        prop = new DynProperty('UpperDeviation', this.UpperDeviation, DynProperty.INTEGER, DynProperty.VISUAL_GROUP);
        prop.propertyComment = Resources.getResource('property.chart.regression.UpperDeviation');
        prop.sortIndex = 2;
        prop.maximalValue = Number.POSITIVE_INFINITY;
        prop.minimalValue = 1;
        props.push(prop);
        prop = new DynProperty('LowerDeviation', this.LowerDeviation, DynProperty.INTEGER, DynProperty.VISUAL_GROUP);
        prop.propertyComment = Resources.getResource('property.chart.regression.LowerDeviation');
        prop.sortIndex = 3;
        prop.minimalValue = Number.NEGATIVE_INFINITY;
        prop.maximalValue = -1;
        props.push(prop);
        prop = new DynProperty('PriceType', this.PriceType, DynProperty.COMBOBOX, DynProperty.VISUAL_GROUP);
        prop.propertyComment = Resources.getResource('property.chart.regression.PriceType');
        prop.sortIndex = 4;
        prop.objectVariants = createContextMenuItemsFromEnum(PriceType, getPriceTypeKey);
        props.push(prop);
        prop = new DynProperty('UpLine', new ColorStyleWidth(this.UpLinePen.Color, this.UpLinePen.DashStyle, this.UpLinePen.Width), DynProperty.COLOR_STYLE_WIDTH, DynProperty.VISUAL_GROUP);
        prop.propertyComment = Resources.getResource('property.chart.regression.UpLine');
        prop.sortIndex = 5;
        props.push(prop);
        prop = new DynProperty('DownLine', new ColorStyleWidth(this.DownLinePen.Color, this.DownLinePen.DashStyle, this.DownLinePen.Width), DynProperty.COLOR_STYLE_WIDTH, DynProperty.VISUAL_GROUP);
        prop.propertyComment = Resources.getResource('property.chart.regression.DownLine');
        prop.sortIndex = 7;
        props.push(prop);
        prop = new DynProperty('AreaTop', this.upBrushColor, DynProperty.COLOR, DynProperty.VISUAL_GROUP);
        prop.propertyComment = Resources.getResource('property.chart.regression.Area.Top');
        prop.sortIndex = 8;
        props.push(prop);
        prop = new DynProperty('AreaBottom', this.downBrushColor, DynProperty.COLOR, DynProperty.VISUAL_GROUP);
        prop.propertyComment = Resources.getResource('property.chart.regression.Area.Bottom');
        prop.sortIndex = 9;
        props.push(prop);
        return props;
    }
    callBack(properties) {
        super.callBack(properties);
        let prop = DynProperty.getPropertyByName(properties, 'UpperDeviation');
        if (prop)
            this.UpperDeviation = prop.value;
        prop = DynProperty.getPropertyByName(properties, 'LowerDeviation');
        if (prop)
            this.LowerDeviation = prop.value;
        prop = DynProperty.getPropertyByName(properties, 'PriceType');
        if (prop && typeof prop.value === 'number')
            this.PriceType = prop.value;
        prop = DynProperty.getPropertyByName(properties, 'UpLine');
        if (prop && prop.value instanceof ColorStyleWidth) {
            const colorStyleWidth = prop.value;
            this.UpLinePen.Color = colorStyleWidth.Color;
            this.UpLinePen.Width = colorStyleWidth.Width;
            this.UpLinePen.DashStyle = colorStyleWidth.Style;
        }
        prop = DynProperty.getPropertyByName(properties, 'DownLine');
        if (prop && prop.value instanceof ColorStyleWidth) {
            const colorStyleWidth = prop.value;
            this.DownLinePen.Color = colorStyleWidth.Color;
            this.DownLinePen.Width = colorStyleWidth.Width;
            this.DownLinePen.DashStyle = colorStyleWidth.Style;
        }
        prop = DynProperty.getPropertyByName(properties, 'AreaTop');
        if (prop)
            this.UpBrushColor = prop.value;
        prop = DynProperty.getPropertyByName(properties, 'AreaBottom');
        if (prop)
            this.DownBrushColor = prop.value;
        this.needRecalculate = true;
    }
}
